import React, { useEffect, useState } from "react";
import { Plus, Trash2, ExternalLink, MapPin } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00E5FF]";

const FIELDS = [
  ["title", "Tour / experience title", "text"],
  ["duration", "Duration (e.g. 90 min)", "text"],
  ["price", "Price (e.g. $25)", "text"],
  ["category", "Type (e.g. Walking Tour, Tasting)", "text"],
  ["meeting_point", "Meeting point", "text"],
  ["booking_url", "Booking link (URL)", "text"],
  ["image", "Cover image URL", "text"],
  ["description", "Description", "textarea"],
  ["gallery", "Photo gallery — one image URL per line", "list"],
  ["highlights", "Highlights — one per line", "list"],
  ["itinerary", "Itinerary — one stop per line (in order)", "list"],
  ["included", "What's included", "textarea"],
];

const LIST_KEYS = ["gallery", "highlights", "itinerary"];

export default function ToursManager() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({});
  const load = () => api.get("/my/tours").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!f.title) { toast.error("Give your tour a title"); return; }
    const payload = { ...f };
    LIST_KEYS.forEach((k) => { payload[k] = (f[k] || "").split("\n").map((s) => s.trim()).filter(Boolean); });
    try { await api.post("/my/tours", payload); toast.success("Tour published — it's live now"); setF({}); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/my/tours/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-white mb-1">Your Tours &amp; Experiences <span className="text-neutral-500 text-base">({items.length})</span></h2>
      <p className="text-neutral-500 text-sm mb-5">Anything you list here publishes live to the public <a href="/tours" target="_blank" rel="noreferrer" className="text-[#00E5FF] inline-flex items-center gap-1">Tours page <ExternalLink size={12} /></a>.</p>
      <form onSubmit={add} className="border border-white/10 bg-[#141414] p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 max-w-3xl">
        {FIELDS.map(([k, label, type]) => (
          <div key={k} className={type === "textarea" || type === "list" ? "sm:col-span-2" : ""}>
            <label className="text-neutral-400 text-xs">{label}</label>
            {type === "textarea" || type === "list"
              ? <textarea rows={type === "list" ? 4 : 3} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`tour-${k}`} className={`mt-1 ${inputCls}`} />
              : <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`tour-${k}`} className={`mt-1 ${inputCls}`} />}
          </div>
        ))}
        <Button type="submit" data-testid="tour-add" className="bg-[#FF0055] hover:bg-[#ff2d74] rounded-none w-fit sm:col-span-2"><Plus size={15} /> Publish Tour</Button>
      </form>
      <div className="space-y-2 max-w-3xl">
        {items.map((t) => (
          <div key={t.id} className="flex items-center justify-between border border-white/10 bg-[#141414] p-3" data-testid={`tour-row-${t.id}`}>
            <div className="min-w-0 flex items-center gap-3">
              {t.image && <img src={t.image} alt="" className="w-12 h-12 object-cover flex-shrink-0" />}
              <div className="min-w-0">
                <div className="text-white text-sm font-medium truncate">{t.title}</div>
                <div className="text-neutral-500 text-xs truncate flex items-center gap-2"><MapPin size={11} /> {t.duration} · {t.price}</div>
              </div>
            </div>
            <button onClick={() => del(t.id)} data-testid={`tour-delete-${t.id}`} className="text-neutral-500 hover:text-[#FF0055] flex-shrink-0"><Trash2 size={16} /></button>
          </div>
        ))}
        {items.length === 0 && <div className="text-neutral-500 text-sm py-6">No tours yet — add your first experience above.</div>}
      </div>
    </div>
  );
}
